import {
  type Column as ReactTableColumn,
  type ColumnOrderState,
  functionalUpdate,
  memo,
  orderColumns,
} from '@tanstack/react-table';
import { isEqual } from 'lodash-es';
import { useCallback, useRef } from 'react';

import type { DataTableColumnOrderProps } from './column-order-types.js';
import {
  canMoveColumnLeft,
  canMoveColumnRight,
  getColumnsToCompareForMove,
  moveColumnLeft,
  moveColumnRight,
} from './column-order-utils.js';
import {
  extractColumnOrderAndGroups,
  validateColumnOrder,
} from './column-order-validation.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import type {
  Column,
  Header,
  RowData,
  Table,
  TableFeature,
  TableOptions,
  Updater,
} from '../../hooks/useTable/types.js';
import { getMemoOptions } from '../../hooks/useTable/utils.js';
import type { DataTableColumnDef, DataTableRowData } from '../../public.api.js';

/**
 * Additional feature implementation for column order.
 * @internal
 */
export const DataTableColumnOrder: TableFeature = {
  createTable<TData extends DataTableRowData>(table: Table<TData>): void {
    // overwrite the order columns fn from react table so that builtin columns are placed correctly
    table._getOrderColumnsFn = memo(
      () => {
        const tableState = table.getState();
        return [
          tableState.columnOrder,
          tableState.grouping,
          table.options.groupedColumnMode,
        ];
      },
      (columnOrder, grouping, groupedColumnMode) =>
        (columns: Column<TData, unknown>[]) => {
          const prefixColumns: Column<TData, unknown>[] = [];
          const suffixColumns: Column<TData, unknown>[] = [];
          let orderedColumns: Column<TData, unknown>[] = columns;

          // If there is no order, return the normal columns
          if (columnOrder.length) {
            const columnsMap = new Map();

            columns.forEach((column) => {
              const builtinPlacement = column.columnDef.meta?.builtinPlacement;
              if (builtinPlacement === 'prefix') {
                prefixColumns.push(column);
              } else if (builtinPlacement === 'suffix') {
                suffixColumns.push(column);
              } else {
                columnsMap.set(column.id, column);
              }
            });

            orderedColumns = [
              ...prefixColumns,
              ...columnOrder.flatMap((id) => columnsMap.get(id) ?? []),
              ...suffixColumns,
            ];
          }

          return orderColumns(
            orderedColumns as ReactTableColumn<TData>[],
            grouping,
            groupedColumnMode,
          ) as Column<TData, unknown>[];
        },
      getMemoOptions(table.options, 'debugCells', 'cell._getOrderColumnsFn'),
    );

    // Helper function that provides an array of all leaf-node columns, excluding builtin columns.
    table.getLeafColumnsWithoutBuiltin = () => {
      return table
        .getAllLeafColumns()
        .filter((leafColumn) => !leafColumn.columnDef.meta?.builtin);
    };

    table.setDraggedHeaderColumn = (column) => {
      table.setState((state) => ({
        ...state,
        draggedHeaderColumn: column,
      }));
    };
  },

  createHeader<TData extends RowData, TValue>(
    header: Header<TData, TValue>,
    table: Table<TData>,
  ) {
    header.getColumnDragAndDropMode = () => {
      const isDragAndDropDisabled =
        table.options.enableColumnOrdering !== true ||
        header.column.columnDef.enableUserActions === false ||
        header.getIsPinned() ||
        (!header.column.getCanMoveColumnLeft() &&
          !header.column.getCanMoveColumnRight());

      if (isDragAndDropDisabled) {
        return 'none';
      }

      if (
        !header.column.getCanColumnActions() &&
        !header.getCanSortByHeaderClick()
      ) {
        return 'only';
      }

      return 'enabled';
    };

    header.getIsDragAndDropDisabled = () => {
      const { draggedHeaderColumn } = table.getState();
      const draggedHeaderParentId = draggedHeaderColumn?.parent?.id;
      const draggedHeaderId = draggedHeaderColumn?.id;
      const isPinnedHeader = !!header.getIsPinned();

      if (draggedHeaderId && isPinnedHeader) {
        return true;
      }

      if (
        draggedHeaderParentId === undefined ||
        draggedHeaderParentId === header.column.id ||
        draggedHeaderParentId === header.column.parent?.id
      ) {
        return false;
      } else {
        return true;
      }
    };
  },

  createColumn<TData extends DataTableRowData, TValue>(
    column: Column<TData, TValue>,
    table: Table<TData>,
  ) {
    column.getCanMoveColumnLeft = () => {
      const columnsToCompare = getColumnsToCompareForMove(table, column);
      return canMoveColumnLeft(column, columnsToCompare);
    };

    column.getCanMoveColumnRight = () => {
      const columnsToCompare = getColumnsToCompareForMove(table, column);
      return canMoveColumnRight(column, columnsToCompare);
    };

    /**
     * Moves the current column one position to the left and updates the table state.
     *
     * Depending on whether the column is a nested column or a standalone column,
     * whether its neighboring columns are pinned, hidden, or nested,
     * we need to consider different scenarios to calculate the new column order correctly.
     *
     * CASE1: Column itself is a nested child column.
     *  - Constraint1: Can only move within the parent group.
     *  - Constraint2: Skip pinned siblings.
     *  - Constraint3: Skip hidden siblings.
     *
     * CASE2: Column is a standalone column or a parent column.
     *  - Constraint1: Skip entire nested columns as a whole.
     *  - Constraint2: Skip pinned columns.
     *  - Constraint3: Skip hidden columns.
     */
    column.moveColumnLeft = () => {
      // Get the current table state needed for column reordering
      const leafColumns = table.getLeafColumnsWithoutBuiltin();
      const currentColumnOrder = table.getState().columnOrder;
      const columnsPinned = table.getState().columnPinning;

      const newColumnOrder = moveColumnLeft(
        column,
        currentColumnOrder,
        columnsPinned,
        leafColumns,
      );
      table.setColumnOrder(newColumnOrder);
    };

    /**
     * Moves the current column one position to the right and updates the table state.
     *
     * Depending on whether the column is a nested column or a standalone column,
     * whether its neighboring columns are pinned, hidden, or nested,
     * we need to consider different scenarios to calculate the new column order correctly.
     *
     * CASE1: Column itself is a nested child column.
     *  - Constraint1: Can only move within the parent group.
     *  - Constraint2: Skip pinned siblings.
     *  - Constraint3: Skip hidden siblings.
     *
     * CASE2: Column is a standalone column or a parent column.
     *  - Constraint1: Skip entire nested columns as a whole.
     *  - Constraint2: Skip pinned columns.
     *  - Constraint3: Skip hidden columns.
     */
    column.moveColumnRight = () => {
      // Get the current table state needed for column reordering
      const leafColumns = table.getLeafColumnsWithoutBuiltin();
      const currentColumnOrder = table.getState().columnOrder;
      const columnsPinned = table.getState().columnPinning;
      const newColumnOrder = moveColumnRight(
        column,
        currentColumnOrder,
        columnsPinned,
        leafColumns,
      );
      table.setColumnOrder(newColumnOrder);
    };
  },
};

/**
 * Configuration hook for the DataTable ColumnOrder feature.
 * @internal
 */
export function useColumnOrder<TData extends DataTableRowData>(
  props: DataTableColumnOrderProps & {
    columns: DataTableColumnDef<TData>[];
  },
  options: TableOptions<TData>,
): void {
  options.enableColumnOrdering = false;

  const { columnOrder: columnOrderFromDefinition, columnGroups } =
    extractColumnOrderAndGroups<TData>(props.columns);

  // save the last valid column order to be able to check the validity of a new one
  const lastValidColumnOrder = useRef<string[] | undefined>(
    columnOrderFromDefinition,
  );
  // save the column order inferred from the column definition to be able to check if it changed
  const lastColumnOrderFromDefinition = useRef<string[] | undefined>(
    columnOrderFromDefinition,
  );

  const [columnOrder, setColumnOrder] = useControllableState({
    value: props.columnOrder,
    // if no order was specified, initialize with column definition order
    defaultValue: props.defaultColumnOrder || columnOrderFromDefinition,
  });

  const onColumnOrderChangeCallback = useCallback(
    (
      updater: Updater<ColumnOrderState>,
      trigger: 'user' | 'internal' = 'user',
    ): void => {
      const nextState = functionalUpdate(
        updater,
        options.state?.columnOrder ?? columnOrder,
      );

      if (!isEqual(nextState, columnOrder)) {
        setColumnOrder(nextState);
        props.onColumnOrderChange?.(nextState, trigger);
      }
    },
    [columnOrder, options.state?.columnOrder, props, setColumnOrder],
  );

  if (!props.columnOrdering) {
    return;
  }

  // uncontrolled, column definition changed - update
  if (
    props.columnOrder === undefined &&
    !isEqual(lastColumnOrderFromDefinition.current, columnOrderFromDefinition)
  ) {
    onColumnOrderChangeCallback(columnOrderFromDefinition, 'internal');
    lastColumnOrderFromDefinition.current = columnOrderFromDefinition;
  } else if (!isEqual(columnOrder, lastValidColumnOrder.current)) {
    // Validate the column order when the current order is not equal
    // with the last valid order or if the current order has different
    // elements than the order extracted from the column definition.
    const validColumnOrder = validateColumnOrder(
      columnOrder,
      columnOrderFromDefinition,
      columnGroups,
    );
    // only update column order if it was invalid
    if (validColumnOrder !== null) {
      onColumnOrderChangeCallback(validColumnOrder, 'internal');
      lastValidColumnOrder.current = validColumnOrder;
    }
  }

  options.enableColumnOrdering = true;
  options.state = { ...options.state, columnOrder };

  options.onColumnOrderChange = onColumnOrderChangeCallback;

  options.originalColumnOrder =
    props.defaultColumnOrder ?? lastValidColumnOrder.current;
}
